import React, { useState, useEffect } from 'react';
import uuid from 'uuid-random';
import {
  FlexBehavior,
  FlexJustify,
  IconButton,
  Spinner,
  Tooltip,
  TooltipPosition,
  ButtonVariant,
  ButtonColor,
  ThemeContext,
  magma,
} from 'react-magma-dom';
import { RouteComponentProps, useLocation, useParams } from '@reach/router';
import { stagesApi } from '../../../API/stages';
import { Collection, StagesData } from '../../../types';
import {
  LessonsListBlock,
  LessonsTypeContainer,
  LessonsTypeHeader,
  LessonsTitle,
  LessonsTypeContent,
  LessonPlanHeader,
  LessonsSubHeader,
  StageTypeRow,
  LessonsContainer,
  FontWeightWrapper,
  StyledCenteredSpinnerWrapper,
} from '../../../components/Styled/StyledWrappers';
import { StyledColoredLine } from '../../../components/Styled';
import { ContactUsError } from '../../../components/ContactUsError';
import { lessonsNavLinks } from '../config';
import { BookLabelName } from '../../../types/exemplars';
import { LessonsHeaderContainer } from '../../../components/LessonsHeader';
import { LessonCollection } from './LessonCollection';
import { QuestionCircleIcon } from 'react-magma-icons';
import { COLORS } from '../../../constants';
import { getLessonAvailability } from '../../../utils/envs';

export const LessonsCollections: React.FC<RouteComponentProps> = (): JSX.Element => {
  const { pathname } = useLocation();
  const params = useParams();

  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stages, setStages] = useState<StagesData[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { data },
        } = await stagesApi.getStages();

        setStages(data);
      } catch (error) {
        setError(!!error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getModifiedChunk = (items: Collection[], stage: string) => {
    const chunk = items.slice();

    const ABookLabel = stage + ' A';
    const BBookLabel = stage + ' B';

    if (chunk.length === 1) {
      const bookType = chunk[0].title.slice(-1);
      if (bookType === 'A') {
        chunk.push({
          collectionId: '',
          title: BBookLabel as BookLabelName,
        });
      } else {
        chunk.unshift({
          collectionId: '',
          title: ABookLabel as BookLabelName,
        });
      }
      return chunk;
    }

    return [
      {
        collectionId: '',
        title: ABookLabel as BookLabelName,
      },
      {
        collectionId: '',
        title: BBookLabel as BookLabelName,
      },
    ];
  };

  const getRows = (collections: Collection[], stage: string, rowSize: number) => {
    const rows = [];

    if (!collections.length) {
      const row = (
        <StageTypeRow>
          {getModifiedChunk(collections, stage).map(collection => (
            <LessonCollection collection={collection} isAccessible={collections.includes(collection)} key={uuid()} />
          ))}
        </StageTypeRow>
      );

      rows.push(row);
    } else {
      for (let i = 0, j = collections.length; i < j; i += rowSize) {
        const collectionChunk =
          collections.slice(i, i + rowSize).length === 2
            ? collections.slice(i, i + rowSize)
            : getModifiedChunk(collections, stage);

        const row = (
          <StageTypeRow key={stage}>
            {collectionChunk.map(collection => (
              <LessonCollection collection={collection} isAccessible={collections.includes(collection)} key={uuid()} />
            ))}
          </StageTypeRow>
        );

        rows.push(row);
      }
    }

    return rows;
  };

  const tooltipContent = (
    <>
      These Lesson Plans have been carefully developed in order to
      <FontWeightWrapper weight={800}> support teachers</FontWeightWrapper> and students in their teaching and
      understanding of the key text types. The content provided caters for a
      <FontWeightWrapper weight={800}> range of student abilities</FontWeightWrapper>.
    </>
  );
  const customTheme = Object.assign({}, magma, {
    tooltip: Object.assign({}, magma.tooltip, {
      fontWeight: 'normal',
      typeScale: magma.typeScale.size03,
    }),
  });

  if (isLoading) {
    return (
      <StyledCenteredSpinnerWrapper behavior={FlexBehavior.container} justify={FlexJustify.center}>
        <Spinner color={COLORS.formBlue} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  } else if (error) {
    return <ContactUsError />;
  }

  return (
    <>
      <StyledColoredLine borderTopColor="#dfdfdf" />
      <LessonsContainer width={962}>
        <LessonsHeaderContainer
          navLinks={lessonsNavLinks.filter(link => link.path === pathname)}
          params={{
            ...params,
            names: ['Find a lesson plan'],
          }}
        />
        <LessonsSubHeader tabIndex={-1}>
          <LessonPlanHeader tabIndex={-1}>
            Select a PM Writing Big Book
            <ThemeContext.Provider value={customTheme}>
              <Tooltip content={tooltipContent} position={TooltipPosition.right} testId="lessons-tooltip">
                <IconButton
                  aria-label=""
                  isFullWidth
                  variant={ButtonVariant.link}
                  color={ButtonColor.secondary}
                  icon={<QuestionCircleIcon />}
                />
              </Tooltip>
            </ThemeContext.Provider>
          </LessonPlanHeader>
        </LessonsSubHeader>
        <LessonsListBlock>
          {stages.map(({ title, items }) => {
            if (!getLessonAvailability(title)) {
              return;
            }

            return (
              <LessonsTypeContainer key={title}>
                <LessonsTypeHeader>
                  <LessonsTitle>{title}</LessonsTitle>
                </LessonsTypeHeader>
                <LessonsTypeContent>{getRows(items, title, 2)}</LessonsTypeContent>
              </LessonsTypeContainer>
            );
          })}
        </LessonsListBlock>
      </LessonsContainer>
    </>
  );
};
